/* eslint-disable react-refresh/only-export-components */
import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U3139InitLU, U3139MinutesSearchRow } from "./U3139Service";

export interface U3139ContextType {
  initLU: U3139InitLU;
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3139MinutesSearchRow>;
}

interface U3139ContextProviderProps {
  initLU: U3139InitLU;
  children: ReactNode;
}

const U3139Context = React.createContext<U3139ContextType | undefined>(undefined);

export const U3139ContextProvider: React.FC<U3139ContextProviderProps> = ({ initLU, children }) => {
  const luStateHandlers = useU2000LUStateHandlers({ initLU: initLU as U3139InitLU & Record<string, unknown> });
  const searchStateHandlers = useU2000SearchStateHandlers({
    luName: "u3139",
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const gridStateHandlers = useU2000GridStateHandlers<U3139MinutesSearchRow>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 25,
  });

  const contextValue = useMemo<U3139ContextType>(
    () => ({
      initLU,
      luStateHandlers,
      searchStateHandlers,
      gridStateHandlers,
    }),
    [initLU, luStateHandlers, searchStateHandlers, gridStateHandlers],
  );

  return <U3139Context.Provider value={contextValue}>{children}</U3139Context.Provider>;
};

export function useU3139Context(): U3139ContextType {
  const context = useContext(U3139Context);
  if (!context) {
    throw new Error("useU3139Context must be used within U3139ContextProvider");
  }
  return context;
}
